import {
  BottomSheetContainer,
  Colors,
  createUseBottomSheetModal,
  Flex,
  HorizontalSpacing,
  IconChevronRight,
  IconHeartFilled,
  IconHeartOutlineWithFill,
  List,
  RemoteImage,
  Spinner,
  Typography,
} from '@banhala/ui-kit';
import { useOnPressFavoriteMarket } from './useOnPressFavoriteMarket';
import React, { useCallback, useState } from 'react';
import type { RecommendedMarket } from 'shared/src/api/marketService/getMarketDetail/types';
import { useLogDisplayMarket } from './useLogDisplayMarket';
import { useLogClickMarket } from './useLogClickMarket';
import { useLogViewFavoriteRecommendedMarketModal } from './useLogViewFavoriteRecommendedMarketModal';
import {
  bridge,
  isNotNil,
  useAsyncEffect,
  useNavigator,
} from 'shared/src/utils';
import { useIsForegroundNow } from 'shared/src/hooks';

const RecommendedMarketView: React.FC<{
  market: RecommendedMarket;
  onPressMarket: () => void;
}> = ({ market, onPressMarket }) => {
  const [favorite, setFavorite] = useState<{
    market_sno: number;
    created_at: string;
  } | null>(market.favorite);
  // const { lifeCycle } = useAppLifeCycle();
  const isForegroundNow = useIsForegroundNow();

  const { action, loading } = useOnPressFavoriteMarket({
    sno: market.sno,
    recommendedMarketList: [],
    listId: '',
    favorite: favorite,
    marketName: market.name,
  });

  const onPressFavoriteMarket = useCallback(() => {
    action().then(result => {
      setFavorite(prev => {
        if (prev) {
          return null;
        }
        return result.favorite;
      });
    });
  }, [action]);

  useAsyncEffect(async () => {
    if (isForegroundNow) {
      const localMarketLike = await bridge.getLocalMarketFavorite();

      if (isNotNil(localMarketLike)) {
        const localFavorite = localMarketLike[market.sno];
        if (isNotNil(localFavorite)) {
          setFavorite(
            localFavorite ? { market_sno: market.sno, created_at: '' } : null,
          );
        }
      }
    }
  }, [isForegroundNow]);

  return (
    <Flex
      key={market.sno}
      width={222}
      shadowStep={'large'}
      borderWidth={1}
      borderColor={'border_tertiary'}
      borderRadius={4}
      backgroundColor={'white'}
      onPress={onPressMarket}
    >
      <Flex.Row gap={2}>
        {market.goods_images.slice(0, 2).map(goods => (
          <RemoteImage
            key={goods.toString()}
            uri={goods}
            width={110}
            height={110 * 1.2}
          />
        ))}
        <Flex
          position={'absolute'}
          top={8}
          right={8}
          onPress={onPressFavoriteMarket}
        >
          {loading ? (
            <Spinner size={24} color={'content_always_white'} />
          ) : isNotNil(favorite) ? (
            <IconHeartFilled fill={Colors.bg_pink_primary} />
          ) : (
            <IconHeartOutlineWithFill />
          )}
        </Flex>
      </Flex.Row>
      <Flex.Row padding={{ horizontal: 8, vertical: 12 }} alignItems={'center'}>
        <Flex borderRadius={20} overflow={'hidden'}>
          <RemoteImage width={40} height={40} uri={market.image} />
        </Flex>
        <HorizontalSpacing size={8} />
        <Flex flex={1} gap={4}>
          <Typography type={'subtitle2'} color={'content_primary'}>
            {market.name}
          </Typography>
          <Typography
            type={'body4'}
            color={'content_tertiary'}
            numberOfLines={1}
            ellipsizeMode={'tail'}
          >
            {market.hash_tag}
          </Typography>
        </Flex>
        <IconChevronRight
          width={20}
          height={20}
          fill={Colors.content_tertiary}
        />
      </Flex.Row>
    </Flex>
  );
};

export const useShowRecommendedMarketsBottomSheet = createUseBottomSheetModal<
  void,
  {
    listId: string;
    marketSno: number;
    marketName: string;
    recommendedMarkets: RecommendedMarket[];
  }
>(({ param }) => {
  const { navigator } = useNavigator();

  const logDisplayMarket = useLogDisplayMarket({
    list_id: param.listId,
    count: param.recommendedMarkets.length,
  });

  const logClickMarket = useLogClickMarket({
    list_id: param.listId,
    count: param.recommendedMarkets.length,
  });

  useLogViewFavoriteRecommendedMarketModal(
    param.marketSno,
    param.recommendedMarkets.length,
  );

  return (
    <BottomSheetContainer hideBorder>
      <Flex padding={{ top: 36, bottom: 24, horizontal: 16 }}>
        <Typography type={'h5'}>
          {param.marketName}스타일과 비슷해요!
        </Typography>
      </Flex>
      <Flex width={'100%'} padding={{ bottom: 16 }}>
        <List
          horizontal={true}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{
            paddingHorizontal: 16,
            gap: 8,
          }}
          style={{ height: 212 }}
          data={param.recommendedMarkets}
          onViewableItemsChanged={logDisplayMarket}
          renderItem={({ item: market, index }) => (
            <RecommendedMarketView
              market={market}
              onPressMarket={() => {
                logClickMarket(market, index);
                navigator.moveToMarketHome({
                  sno: market.sno,
                });
              }}
            />
          )}
        ></List>
      </Flex>
    </BottomSheetContainer>
  );
});
